import React from 'react'

import tw, { css, styled } from 'twin.macro'
import PropTypes from 'prop-types'
import { graphql, Link } from 'gatsby'
import Layout from '../components/layout'
import Meta from '../components/meta'
import NewsSidebar from '../components/sidebar'
import Pagination from '../components/pagination'
import Maytronics from '../components/svg/maytronics'
import localeCheck from '../components/utils/locale-check'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/react-hooks'
import { Client } from '../apollo/client'
import Spinner from '../components/utils/spinner'

const MainSection = styled.section`
	${tw`py-12 md:py-20 w-full`}
	background-color: #eeeeee;
`
const CategorySidebarContainer = tw.div`flex flex-col md:flex-row md:px-4 lg:w-11/12 xl:w-4/5 xxl:w-8/12 m-auto`
const CategoryContainer = tw.div`flex flex-col w-full px-4 md:w-2/3 xl:w-3/4 md:pr-4 lg:pr-12`
const Main = tw.div`w-full mb-16 md:mb-0`

const HeroSection = tw.section`flex flex-col bg-dark-grayish-blue w-full py-16 items-center`
const Category = tw.h1`text-white font-display tracking-widest uppercase text-2xl font-bold`

const PostTitle = tw.h3`font-display text-2xl font-bold mb-4`
const PostDescription = tw.p`font-display mb-4`
const PostDateTime = tw.p`font-display mb-4 mt-2`
const Continue = tw.button`border-2 border-dolphin-gray hover:border-dolphin-gray bg-dolphin-gray hover:bg-transparent hover:text-dolphin-gray text-white py-2 px-6 rounded uppercase`

const PostEntries = styled.div`
	${tw`flex flex-col lg:flex-row mb-8 bg-white`}
	box-shadow: 2px 5px 3px 0px rgba(0, 0, 0, 0.2);
`
const ImageContainer = styled.div`
	background: url('${({ bg }) => (bg ? bg : '')}');
	${tw`w-full lg:w-6/12 bg-center bg-no-repeat bg-contain flex items-center justify-center ml-4`}
	${({ bg }) => (bg ? '' : tw`px-4`)};
	height: auto;
	min-height: 300px;
	max-height: 500px;
	img {
		${tw`m-auto`}
	}
`
const PostDetails = tw.div`flex flex-col w-full lg:w-6/12 ml-0 lg:ml-4 px-4 lg:px-2 py-6`
const TagContainer = tw.div`flex flex-row flex-wrap`
const TagItem = styled.p`
	${tw`pr-2 mb-2 font-display text-sm flex items-center w-auto`}

	span {
		${tw`bg-blue-500 text-white px-2 py-1 w-full`}
	}
`

const CategoryPageTemplate = ({
	data: { data },
	pageContext: {
		slug,
		currentPage,
		numPages,
		node_locale: categoryLocale,
		defaultLocale,
		skip,
	},
}) => {
	const locale = categoryLocale
	// const getNode = filteredArray => {
	// 	return filteredArray.map((item, i) => {
	// 		return item.node
	// 	})
	// }
	// const otherNode = getNode(data.edges)
	// let filteredPostByLocale = otherNode.filter(x => {
	// 	if (x.node_locale === locale) {
	// 		return x
	// 	}
	// })
	const paginationLinkSlug = slug
		.toLowerCase()
		.split(' ')
		.join('-')
	const arr = Array.from({ length: numPages })
	const checkedLocale = localeCheck(categoryLocale, defaultLocale)

	const APOLLO_FILTERED_AND_PAGINATED_CATEGORY = gql`
		query FilteredAndPaginatedCategory(
			$slug: String!
			$skip: Int!
			$limit: Int!
			$node_locale: String!
			$country: [String]!
		) {
			postCollection(
				limit: $limit
				skip: $skip
				where: { category: $slug, countryAvailability_contains_some: $country }
				locale: $node_locale
				order: dateAndTime_DESC
			) {
				items {
					seoTitle
					slug
					dateAndTime
					countryAvailability
					seoDescription
					tag
					image {
						url
					}
				}
				total
			}
		}
	`

	const countryLocale = categoryLocale.slice(3)
	const { loading, error, data: apolloData } = useQuery(
		APOLLO_FILTERED_AND_PAGINATED_CATEGORY,
		{
			variables: {
				skip: skip,
				slug: slug,
				limit: 3,
				node_locale: categoryLocale,
				country: [countryLocale],
			},
			client: Client,
		}
	)

	const paginate =
		apolloData != undefined ? apolloData.postCollection.total : []
	const pagesTotal = Array.from({ length: Math.ceil(paginate / 3) })
	const desc = `${slug} | ${locale} | ${currentPage}`
	return (
		<Layout>
			<Meta title={`News ${slug}`} lang={locale} description={desc}/>

			<HeroSection>
				<Category>CATEGORY: {slug}</Category>
			</HeroSection>
			<MainSection>
				<CategorySidebarContainer>
					<CategoryContainer>
						<Main>
							{!loading ? (
								apolloData != undefined ? (
									apolloData.postCollection.items.map((entry, i) => {
										const {
											seoTitle,
											seoDescription,
											slug: postSlug,
											tag,
											dateAndTime,
											image,
										} = entry
										const options = {
											year: 'numeric',
											month: 'long',
											day: 'numeric',
										}
										const newDate = new Date(dateAndTime)
										return (
											<PostEntries key={i}>
												<ImageContainer bg={image ? image.url : ''}>
													{image ? (
														''
													) : (
														<Maytronics width="100%" height="250" />
													)}
												</ImageContainer>
												<PostDetails>
													<PostTitle>{seoTitle}</PostTitle>
													<PostDescription>{seoDescription}</PostDescription>
													<TagContainer>
														{tag
															? tag.map((tagItem, i) => {
																	return (
																		<TagItem key={i}>
																			<span>{tagItem}</span>
																		</TagItem>
																	)
															  })
															: ''}
													</TagContainer>
													<PostDateTime>
														{newDate.toLocaleDateString(
															categoryLocale,
															options
														)}
													</PostDateTime>
													<Link to={`${checkedLocale}/${postSlug}/`}>
														<Continue>Continue Reading</Continue>
													</Link>
												</PostDetails>
											</PostEntries>
										)
									})
								) : (
									''
								)
							) : (
								<Spinner />
							)}
						</Main>

						<Pagination
							currentPage={currentPage}
							pageLength={pagesTotal.length}
							slug={paginationLinkSlug}
							pageArray={pagesTotal}
							checkedLocale={checkedLocale}
						/>
					</CategoryContainer>
					<NewsSidebar
						removeRecent
						categoryLocale={categoryLocale}
						checkedLocale={checkedLocale}
					/>
				</CategorySidebarContainer>
			</MainSection>
		</Layout>
	)
}

export const CategoryTemplateQuery = graphql`
	query FilteredAndPaginatedCategory(
		$slug: String!
		$skip: Int!
		$limit: Int!
		$node_locale: String!
	) {
		data: allContentfulPost(
			filter: {
				category: { eq: $slug }
				slug: { ne: null }
				node_locale: { eq: $node_locale }
			}
			skip: $skip
			limit: $limit
			sort: { fields: dateAndTime, order: DESC }
		) {
			edges {
				node {
					seoTitle
					node_locale
					slug
					dateAndTime(formatString: "DD MMMM, YYYY")
					countryAvailability
					seoDescription
					tag
					image {
						file {
							url
						}
					}
				}
			}
		}
	}
`

export default CategoryPageTemplate
